import type { ReactNode } from "react";
import { ScrollText, SearchCheck, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { chapterMeta } from "@/engine/chapters";
import { GlassCard, GradientText, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";

const [FROM] = chapterMeta(4).accent;

/** Amber mono token for broken-plumbing artefacts. */
function Chip({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-md bg-accent-amber/15 px-2 py-0.5 font-mono text-[0.85em] text-accent-amber">
      {children}
    </code>
  );
}

interface Row {
  icon: LucideIcon;
  title: string;
  body: ReactNode;
  highlight?: boolean;
}

const ROWS: readonly Row[] = [
  {
    icon: SearchCheck,
    title: "Start in Search Console",
    body: (
      <>
        <span className="font-semibold text-white">Coverage / Page indexing</span>, sitemaps, and
        mobile usability flag real issues{" "}
        <span className="font-semibold text-white">worth fixing first</span>.
      </>
    ),
  },
  {
    icon: Wrench,
    title: "Fix what's actually broken",
    highlight: true,
    body: (
      <>
        Fix <Chip>404 chains</Chip>, <Chip>soft-404s</Chip>, and accidental <Chip>noindex</Chip> on
        important templates —{" "}
        <GradientText className="font-semibold">before chasing perfect scores</GradientText>.
      </>
    ),
  },
  {
    icon: ScrollText,
    title: "Watch the crawl budget",
    body: (
      <>
        Log or server data shows <span className="font-semibold text-white">crawl-budget waste</span>{" "}
        on faceted or parameter URLs (<Chip>?sort=price&amp;page=12</Chip>) on large sites.
      </>
    ),
  },
];

export function TechnicalPracticeSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={4} title="Technical SEO in practice">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-12">
        <RevealList active={active} stagger={0.16} className="flex flex-col gap-7">
          {ROWS.map(({ icon: Icon, title, body, highlight }) => (
            <RevealItem key={title}>
              <GlassCard
                glow={highlight}
                className={`flex items-start gap-7 p-8 ${highlight ? "border-white/20" : ""}`}
              >
                <span
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/10"
                  style={{ backgroundColor: `${FROM}1c` }}
                >
                  <Icon className="h-8 w-8" strokeWidth={1.75} style={{ color: FROM }} />
                </span>
                <span className="block">
                  <span className="block font-display text-3xl font-semibold text-white">
                    {title}
                  </span>
                  <span className="mt-3 block text-2xl leading-relaxed text-white/85">{body}</span>
                </span>
              </GlassCard>
            </RevealItem>
          ))}
        </RevealList>

        <Reveal active={active} delay={0.9}>
          <p className="text-center font-display text-3xl text-white/70">
            <span className="font-semibold text-white">Real issues first.</span> Perfect scores
            later.
          </p>
        </Reveal>
      </div>
    </SlideShell>
  );
}
